const WCAG_MIN_CONTRAST = 3;

const toSRgb = (value: number): number => {
  const v = value / 255;

  return v <= 0.04045 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
};

const getLuminance = (r: number, g: number, b: number): number => {
  return 0.2126 * toSRgb(r) + 0.7152 * toSRgb(g) + 0.0722 * toSRgb(b);
};

const getContrastRatio = (l1: number, l2: number): number => {
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);

  return (lighter + 0.05) / (darker + 0.05);
};

const hexToChannels = (hex: string): [number, number, number] => {
  const h = hex.startsWith('#') ? hex.slice(1) : hex;

  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16)
  ];
};

const luminanceFromHex = (hex: string): number => {
  const [r, g, b] = hexToChannels(hex);

  return getLuminance(r, g, b);
};

/**
 * Returns preferred color if it has enough contrast (>= WCAG_MIN_CONTRAST),
 * otherwise returns the color with better contrast.
 */
export function getPreferredContrastColor(
  baseColor: string,
  preferred: string,
  fallback: string
): string {
  const baseLuminance = luminanceFromHex(baseColor);
  const preferredContrast = getContrastRatio(
    baseLuminance,
    luminanceFromHex(preferred)
  );

  if (preferredContrast >= WCAG_MIN_CONTRAST) {
    return preferred;
  }

  const fallbackContrast = getContrastRatio(
    baseLuminance,
    luminanceFromHex(fallback)
  );

  return preferredContrast >= fallbackContrast ? preferred : fallback;
}
